import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import Decription from "components/features/TwoColDescriptionProducts";
import { PrimaryButton } from "components/misc/Buttons.js";
import { createEncryptedCard } from "services/payment.services.js";
import { makePayment } from "adapters/payment.adapters.js";

// Import images
import alertsImage from "assets/alerts_image.png";

const FormContainer = styled.div`
  ${tw`max-w-md mx-auto bg-white p-8 rounded-lg shadow-lg`}
`;

const FormGroup = styled.div`
  ${tw`mb-6`}
`;

const Label = styled.label`
  ${tw`block text-sm font-medium text-gray-700 mb-2`}
`;

const Input = styled.input`
  ${tw`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
`;

const Row = styled.div`
  ${tw`flex gap-4`}
`;

const Column = styled.div`
  ${tw`flex-1`}
`;

const SubmitButton = styled(PrimaryButton)`
  ${tw`w-full mt-4`}
`;

const LoadingModal = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}
`;

const LoadingContent = styled.div`
  ${tw`bg-white p-8 rounded-lg shadow-lg text-center max-w-sm w-full mx-4`}
`;

const Spinner = styled.div`
  ${tw`animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4`}
`;

const LoadingText = styled.p`
  ${tw`text-gray-700 text-lg font-medium`}
`;

const LoadingSubText = styled.p`
  ${tw`text-gray-500 text-sm mt-2`}
`;

const ProductBox = styled.div`
  ${tw`bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg mb-6 text-center`}
`;

const ProductTitle = styled.h3`
  ${tw`text-xl font-bold mb-2`}
`;

const ProductPrice = styled.p`
  ${tw`text-3xl font-bold`}
`;

const ProductDescription = styled.p`
  ${tw`text-blue-100 mt-2`}
`;

export default () => {
    const [formData, setFormData] = useState({
        nome: '',
        cpf: '',
        email: '',
        numeroCartao: '',
        mesVencimento: '',
        anoVencimento: '',
        cvc: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const formatCPF = (value) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const formatCardNumber = (value) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{4})(\d)/, '$1 $2')
            .replace(/(\d{4})(\d)/, '$1 $2')
            .replace(/(\d{4})(\d)/, '$1 $2')
            .replace(/(\d{4})\d+?$/, '$1');
    };

    const handleCPFChange = (e) => {
        const formatted = formatCPF(e.target.value);
        setFormData(prev => ({
            ...prev,
            cpf: formatted
        }));
    };

    const handleCardNumberChange = (e) => {
        const formatted = formatCardNumber(e.target.value);
        setFormData(prev => ({
            ...prev,
            numeroCartao: formatted
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setLoadingMessage('Processando dados do cartão...');
        
        try {
            const encryptedCard = await createEncryptedCard(
                formData.nome,
                formData.numeroCartao.replace(/\s+/g, ''),
                formData.mesVencimento,
                formData.anoVencimento,
                formData.cvc
            );
            
            setLoadingMessage('Processando pagamento...');
            
            const response = await makePayment(
                encryptedCard.encryptedCard,
                formData.nome,
                formData.email,
                formData.cpf.replace(/\D/g, ''),
            );
            
            setLoadingMessage('Pagamento aprovado!');
            console.log('Payment response:', response.data);
            
            // Hide loading after a brief success message
            setTimeout(() => {
                setIsLoading(false);
                // Clear all form fields after successful payment
                setFormData({
                    nome: '',
                    cpf: '',
                    email: '',
                    numeroCartao: '',
                    mesVencimento: '',
                    anoVencimento: '',
                    cvc: ''
                });
                // Here you could redirect or show success message
            }, 2000);
            
        } catch (error) {
            setLoadingMessage('Erro no processamento');
            console.error('Payment error:', error);
            
            // Hide loading after error
            setTimeout(() => {
                setIsLoading(false);
            }, 3000);
        }
    };

    const content = (
        <FormContainer>
            <ProductBox>
                <ProductTitle>Pacote de Alertas</ProductTitle>
                <ProductPrice>R$ 10,00</ProductPrice>
                <ProductDescription>100 alertas para WhatsApp e E-mail</ProductDescription>
            </ProductBox>
            
            <form onSubmit={handleSubmit}>
                <FormGroup>
                    <Label htmlFor="nome">Nome Completo</Label>
                    <Input
                        type="text"
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleInputChange}
                        placeholder="Digite seu nome completo"
                        required
                    />
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="cpf">CPF</Label>
                    <Input
                        type="text"
                        id="cpf"
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleCPFChange}
                        placeholder="000.000.000-00"
                        maxLength="14"
                        required
                    />
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="email">E-mail</Label>
                    <Input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="seu@email.com"
                        required
                    />
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="numeroCartao">Número do Cartão de Crédito</Label>
                    <Input
                        type="text"
                        id="numeroCartao"
                        name="numeroCartao"
                        value={formData.numeroCartao}
                        onChange={handleCardNumberChange}
                        placeholder="0000 0000 0000 0000"
                        maxLength="19"
                        required
                    />
                </FormGroup>

                <Row>
                    <Column>
                        <FormGroup>
                            <Label htmlFor="mesVencimento">Mês</Label>
                            <Input
                                type="number"
                                id="mesVencimento"
                                name="mesVencimento"
                                value={formData.mesVencimento}
                                onChange={handleInputChange}
                                placeholder="MM"
                                min="1"
                                max="12"
                                maxLength="2"
                                required
                            />
                        </FormGroup>
                    </Column>
                    <Column>
                        <FormGroup>
                            <Label htmlFor="anoVencimento">Ano</Label>
                            <Input
                                type="number"
                                id="anoVencimento"
                                name="anoVencimento"
                                value={formData.anoVencimento}
                                onChange={handleInputChange}
                                placeholder="AAAA"
                                min="2024"
                                max="2040"
                                maxLength="4"
                                required
                            />
                        </FormGroup>
                    </Column>
                    <Column>
                        <FormGroup>
                            <Label htmlFor="cvc">CVC</Label>
                            <Input
                                type="text"
                                id="cvc"
                                name="cvc"
                                value={formData.cvc}
                                onChange={handleInputChange}
                                placeholder="123"
                                maxLength="4"
                                pattern="[0-9]{3,4}"
                                required
                            />
                        </FormGroup>
                    </Column>
                </Row>

                <SubmitButton type="submit" disabled={isLoading}>
                    {isLoading ? 'Processando...' : 'Finalizar Compra'}
                </SubmitButton>
            </form>
        </FormContainer>
    )
    return (
        <>
            <Decription
                imageSrc={[alertsImage]}
                imageBorder={false}
                imageShadow={false}
                imageDecoratorBlob={true}
                description={[]}
                descriptionRight={[
                    "Aqui você poderá comprar créditos para enviar alertas de WhatsApp e E-mail através da nossa plataforma. Preencha o formulário ao lado com seus dados e informações do cartão de crédito para concluir a compra com segurança.","Após a confirmação do pagamento, os créditos serão adicionados à sua conta e o sensor enviará notificações automaticamente. Se tiver alguma dúvida ou precisar de assistência, nossa equipe de suporte está à disposição para ajudar.",
                ]}
                content={content}
                columns60x40={true}
                heading="Comprar Créditos de Alertas para Sensores mTs (WhatsApp e E-mail)"
            />
            
            {isLoading && (
                <LoadingModal>
                    <LoadingContent>
                        <Spinner />
                        <LoadingText>{loadingMessage}</LoadingText>
                        <LoadingSubText>
                            {loadingMessage.includes('Processando dados') && 'Criptografando informações do cartão...'}
                            {loadingMessage.includes('Processando pagamento') && 'Aguarde, validando com operadora...'}
                            {loadingMessage.includes('aprovado') && 'Créditos serão adicionados à sua conta!'}
                            {loadingMessage.includes('Erro') && 'Tente novamente em alguns instantes.'}
                        </LoadingSubText>
                    </LoadingContent>
                </LoadingModal>
            )}
        </>
    );
}
